
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUsage, UsageRecord } from '@/services/channelService';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

type IAUsageProps = {
    userId: string;
    channelId: number;
}

const IAUsage = ({userId, channelId}: IAUsageProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [month, setMonth] = useState<number | undefined>(undefined);
    const [year, setYear] = useState<number | undefined>(undefined);

    const startTour = () => {
        const driverObj = driver({
            showProgress: true,
            showButtons: ['next', 'previous', 'close'],
            steps: [
                {
                    element: '#ia-usage-header',
                    popover: {
                        title: t('aiUsage'),
                        description: t('tourAIUsageDesc') || 'Esta sección te permite ver el historial de uso de IA del canal, incluyendo tokens consumidos y costos estimados.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#usage-filters',
                    popover: {
                        title: t('filters'),
                        description: t('tourUsageFiltersDesc') || 'Usa estos filtros para buscar registros por año, mes y cantidad de resultados por página.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#usage-table',
                    popover: {
                        title: t('usageTable'),
                        description: t('tourUsageTableDesc') || 'Esta tabla muestra el detalle de cada uso de IA: modelo utilizado, tokens de entrada y salida, costo estimado y fecha.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#total-cost',
                    popover: {
                        title: t('totalCost'),
                        description: t('tourTotalCostDesc') || 'Aquí se muestra el costo total estimado de todos los registros visibles en la página actual.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#usage-pagination',
                    popover: {
                        title: t('pagination'),
                        description: t('tourPaginationDesc') || 'Usa estos controles para navegar entre las diferentes páginas de registros.',
                        side: "top",
                        align: 'start'
                    }
                }
            ]
        });

        driverObj.drive();
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ['usage', userId, channelId, page, limit, month, year],
        queryFn: () => getUsage({ userId, channelId, page, limit, month, year }),
        enabled: !!userId && !!channelId && isOpen,
    });

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const months = [
        { value: 1, label: 'Enero' },
        { value: 2, label: 'Febrero' },
        { value: 3, label: 'Marzo' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Mayo' },
        { value: 6, label: 'Junio' },
        { value: 7, label: 'Julio' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Septiembre' },
        { value: 10, label: 'Octubre' },
        { value: 11, label: 'Noviembre' },
        { value: 12, label: 'Diciembre' },
    ];

    const handleClearFilters = () => {
        setMonth(undefined);
        setYear(undefined);
        setPage(1);
    };

    const calculateCost = (record: UsageRecord) => {
        const inputCost = record.priceMillionInputToken 
            ? (record.inputTokens / 1_000_000) * record.priceMillionInputToken 
            : 0;
        const outputCost = record.priceMillionOutputToken 
            ? (record.outputTokens / 1_000_000) * record.priceMillionOutputToken 
            : 0;
        return (inputCost + outputCost).toFixed(6);
    };

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden mt-6">
            {/* Accordion Header */}
            <div id="ia-usage-header" className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 flex-1"
                >
                    <span className="text-lg font-semibold">{t('aiUsage') || 'Uso de IA'}</span>
                    <svg
                        className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                <button
                    onClick={startTour}
                    className="rounded-full p-1.5 text-gray-600 transition hover:bg-gray-200 hover:text-indigo-600"
                    title={t('startTour') || 'Iniciar tour'}
                >
                    <HelpCircle className="h-5 w-5" />
                </button>
            </div>

            {/* Accordion Content */}
            {isOpen && (
                <div className="p-4 space-y-4">
                    {/* Filtros */}
                    <div id="usage-filters" className="flex flex-wrap gap-4 items-end">
                        <div className="flex-1 min-w-[150px]">
                            <label className="text-sm font-medium mb-2 block">{t('year') || 'Año'}</label>
                            <select 
                                value={year?.toString() || ''} 
                                onChange={(e) => {
                                    setYear(e.target.value ? parseInt(e.target.value) : undefined);
                                    setPage(1);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">{t('all') || 'Todos'}</option>
                                {years.map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1 min-w-[150px]">
                            <label className="text-sm font-medium mb-2 block">{t('month') || 'Mes'}</label>
                            <select 
                                value={month?.toString() || ''} 
                                onChange={(e) => {
                                    setMonth(e.target.value ? parseInt(e.target.value) : undefined);
                                    setPage(1);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">{t('all') || 'Todos'}</option>
                                {months.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1 min-w-[150px]">
                            <label className="text-sm font-medium mb-2 block">{t('recordsPerPage') || 'Registros por página'}</label>
                            <select 
                                value={limit.toString()} 
                                onChange={(e) => {
                                    setLimit(parseInt(e.target.value));
                                    setPage(1);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>

                        <button 
                            onClick={handleClearFilters}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            {t('clearFilters') || 'Limpiar filtros'}
                        </button>
                    </div>

                    {/* Tabla */}
                    {isLoading ? (
                        <div className="text-center py-8">{t('loading') || 'Cargando...'}</div>
                    ) : error ? (
                        <div className="text-center py-8 text-red-500">
                            {t('errorLoadingData') || 'Error al cargar los datos'}
                        </div>
                    ) : !data?.data || data.data.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {t('noDataAvailable') || 'No hay datos disponibles'}
                        </div>
                    ) : (
                        <>
                            <div id="usage-table" className="border rounded-lg overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('id') || 'ID'}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('model') || 'Modelo'}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('inputTokens') || 'Tokens Entrada'}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('outputTokens') || 'Tokens Salida'}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('estimatedCost') || 'Costo Estimado'}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('date') || 'Fecha'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {data.data.map((record) => (
                                            <tr key={record.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">{record.id}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{record.model}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">{record.inputTokens.toLocaleString()}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">{record.outputTokens.toLocaleString()}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">${calculateCost(record)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    {new Date(record.createdAt).toLocaleString('es-ES')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginación */}
                            <div id="total-cost">
                                <p className=' text-gray-700'>{t('totalCost') || 'Total coste'}: ${data.data.reduce((acc, record) => acc + Number(calculateCost(record)), 0).toFixed(6)}</p>
                                <div className="my-4">
                                    <hr />
                                </div>
                            </div>
                            <div id="usage-pagination" className="flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    {t('showing') || 'Mostrando'} {((page - 1) * limit) + 1} {t('to') || 'a'}{' '}
                                    {Math.min(page * limit, data.pagination.total)} {t('of') || 'de'}{' '}
                                    {data.pagination.total} {t('records') || 'registros'}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                        className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 text-sm"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        {t('previous') || 'Anterior'}
                                    </button>
                                    <div className="text-sm">
                                        {t('page') || 'Página'} {page} {t('of') || 'de'} {data.pagination.totalPages}
                                    </div>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={page === data.pagination.totalPages}
                                        className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 text-sm"
                                    >
                                        {t('next') || 'Siguiente'}
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
 
export default IAUsage;